<template>
  <el-dialog :title="dialogTitle" v-model="dialogVisibility" :close-on-click-modal="false"
    :close-on-press-escape="false" :show-close="false" size="large">
    <el-alert v-if="errorMessage" :title="errorMessage" type="error" :closable="true" @close="errorMessage = null"
      style="margin-bottom: 15px;"></el-alert>

    <!-- Manual send trigger result. The endpoint returns 200 even when
         recipients were skipped or the send failed, so show the per-recipient
         outcomes rather than a bare success message. -->
    <el-alert v-if="runSummary" :title="runSummary.headline" :type="runSummary.clean ? 'success' : 'warning'"
      :closable="true" @close="runSummary = null" style="margin-bottom: 15px;">
      <div v-for="(line, index) in runSummary.lines" :key="index" class="run-summary-line">{{ line }}</div>
    </el-alert>

    <!-- List view -->
    <div v-if="view === 'list'" v-loading="loadingGroups">
      <div class="subscriptions-toolbar">
        <el-button data-testid="add-group" size="small" type="primary" :disabled="atGroupLimit" @click="openCreate">+ Add Group</el-button>
        <span class="subscriptions-quota" :class="{ 'subscriptions-quota--full': atGroupLimit }">
          {{ groups.length }}/{{ groupLimit }} groups used<template v-if="atGroupLimit"> — limit reached</template>
        </span>
      </div>
      <el-table :data="groups" stripe style="width: 100%">
        <el-table-column prop="name" label="Name"></el-table-column>
        <el-table-column label="Report" width="180">
          <template slot-scope="scope">{{ scope.row.reportType && scope.row.reportType.label }}</template>
        </el-table-column>
        <el-table-column label="Recipients">
          <template slot-scope="scope">
            <el-tooltip placement="top" :content="recipientEmails(scope.row)">
              <span>{{ recipientSummary(scope.row) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="Frequency" width="120">
          <template slot-scope="scope">{{ prettifyFrequency(scope.row.frequency) }}</template>
        </el-table-column>
        <el-table-column label="Updated" width="120">
          <template slot-scope="scope">{{ formatDate(scope.row.updated) }}</template>
        </el-table-column>
        <el-table-column label="Actions" width="160">
          <template slot-scope="scope">
            <el-button data-testid="edit-group" size="mini" @click="openEdit(scope.row)">Edit</el-button>
            <el-button data-testid="delete-group" size="mini" type="danger" plain @click="confirmDelete(scope.row)">Delete</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="!loadingGroups && groups.length === 0" class="subscriptions-empty">
        No subscription groups yet. Add one to start receiving scheduled Hand Hygiene compliance reports.
      </div>
    </div>

    <!-- Create / Edit form view -->
    <el-form v-if="view === 'form'" v-loading="loadingUsers || loadingReportTypes" ref="subscriptionForm" :model="form" :rules="formRules"
      label-width="120px">
      <el-form-item label="Name" prop="name">
        <el-input v-model="form.name" :maxlength="255" placeholder="e.g. HH Weekly Compliance"></el-input>
      </el-form-item>
      <!-- <el-form-item label="Report Type" prop="reportTypeId">
        <el-select v-model="form.reportTypeId" placeholder="Select a report" style="width: 100%;">
          <el-option v-for="reportType in reportTypes" :key="reportType.id" :label="reportType.label"
            :value="reportType.id"></el-option>
        </el-select>
      </el-form-item> -->
      <el-form-item label="Frequency" prop="frequency">
        <el-radio-group v-model="form.frequency">
          <el-radio-button v-for="freq in frequencies" :key="freq" :label="freq">{{ prettifyFrequency(freq)
            }}</el-radio-button>
        </el-radio-group>
      </el-form-item>
      <el-form-item label="Recipients" prop="recipientUserIds">
        <el-select v-model="form.recipientUserIds" multiple filterable clearable remote
          :remote-method="searchUsers" :loading="searchingUsers" @visible-change="onRecipientDropdownToggle"
          placeholder="Search users by name or username" style="width: 100%;">
          <el-option v-for="user in recipientOptions" :key="user.id" :label="userLabel(user)"
            :value="user.id" :disabled="!user.email"></el-option>
        </el-select>
        <div class="recipients-actions">
          <el-button data-testid="select-all-recipients" size="mini" :disabled="allRecipientsSelected" @click="selectAllRecipients">Select All</el-button>
          <el-button data-testid="reset-recipients" size="mini" :disabled="form.recipientUserIds.length === 0" @click="resetRecipientSelection">Reset Selection</el-button>
        </div>
      </el-form-item>
    </el-form>

    <div slot="footer">
      <template v-if="view === 'list'">
        <el-button data-testid="close-subscriptions" @click="close">Close</el-button>
      </template>
      <template v-else>
        <el-button data-testid="cancel-group" @click="backToList" :disabled="saving">Cancel</el-button>
        <el-button data-testid="save-group" type="primary" @click="save" :loading="saving" :disabled="!formValid || loadingReportTypes">Save</el-button>
        <el-button data-testid="test-group" v-if="showTestBtn" type="info" @click="test" :loading="testing" :disabled="!formValid || loadingReportTypes || saving" style="margin-left: 2rem;">Save & Test</el-button>
      </template>
    </div>
  </el-dialog>
</template>

<script>
const FREQUENCIES = ["WEEKLY", "MONTHLY", "QUARTERLY"];
// GET /uam/users caps `size` at 1000. The picker preloads one page as its
// starting option pool and then searches server-side for anything beyond it.
const USER_PRELOAD_SIZE = 1000;
const USER_SEARCH_SIZE = 100;
import auth from "../../auth";

export default {
  name: "SubscriptionsDialog",
  data() {
    return {
      dialogVisibility: true,
      view: "list",
      groups: [],
      loadingGroups: false,
      // Option pool for the recipient picker: the preloaded first page, plus
      // anything pulled in by a remote search or seeded from a group's saved
      // recipients. Merged by id, never pruned — an option that has rendered
      // once keeps its label on the selected tag (el-select caches it).
      users: [],
      loadingUsers: false,
      // Current text in the recipient picker. el-select runs in `remote` mode
      // so its own local filtering is off; recipientOptions applies this.
      userQuery: "",
      searchingUsers: false,
      // Bumped per search so responses from superseded keystrokes are dropped.
      userSearchToken: 0,
      // Ids the API returned for the current query. Held separately so a
      // server-side match is always offered, even when the local predicate
      // below wouldn't have matched it.
      searchResultIds: [],
      reportTypes: [],
      loadingReportTypes: false,
      saving: false,
      testing: false,
      errorMessage: null,
      runSummary: null,
      editingGroupId: null,
      form: {
        name: "",
        frequency: "WEEKLY",
        recipientUserIds: [],
        reportTypeId: null
      },
      // trigger: "submit" never matches the blur/change events el-form-item
      // listens for, so inline messages only appear when save() explicitly
      // calls validate() — not while the user is filling in the form.
      formRules: {
        name: [{ required: true, message: "Name is required", trigger: "submit" }],
        frequency: [{ required: true, message: "Frequency is required", trigger: "submit" }],
        recipientUserIds: [{ type: "array", required: true, min: 1, message: "Select at least one recipient", trigger: "submit" }],
        reportTypeId: [{ type: "number", required: true, message: "Report type is required", trigger: "submit" }]
      },
      frequencies: FREQUENCIES,
      groupLimit: 0
    };
  },
  computed: {
    groupsUrl: () => `${window.CONFIG.scheduled_reports_api}/subscription-groups`,
    reportTypesUrl: () => `${window.CONFIG.scheduled_reports_api}/report-types`,
    usersUrl: () => `${window.CONFIG.uam_api}/users`,
    dialogTitle() {
      if (this.view === "form") {
        return this.editingGroupId ? "Edit Subscription Group" : "Create Subscription Group";
      }
      return "Manage Report Subscriptions";
    },
    atGroupLimit() {
      return this.groups.length >= this.groupLimit;
    },
    // Users that can actually be saved as recipients — the API rejects any
    // without an email. Drives Select All, not what the dropdown lists.
    recipientPool() {
      return this.users.filter(user => user.email);
    },
    // What the dropdown lists. A row qualifies if the API returned it for the
    // current query OR it matches locally: the server can only match
    // `username` while the local predicate can only see what's loaded, so
    // either alone drops results the other found. Email-less users stay in the
    // list as disabled rows rather than disappearing with no explanation.
    recipientOptions() {
      const query = this.userQuery.trim().toLowerCase();
      if (!query) return this.users;
      return this.users.filter(user =>
        this.searchResultIds.indexOf(user.id) >= 0 || this.userMatchesQuery(user, query)
      );
    },
    allRecipientsSelected() {
      return this.recipientPool.length > 0 && this.form.recipientUserIds.length === this.recipientPool.length;
    },
    // Mirrors formRules; drives the Save button's disabled state without
    // triggering the inline validation messages.
    formValid() {
      return this.form.name.trim().length > 0 && !!this.form.frequency && this.form.recipientUserIds.length > 0 && this.form.reportTypeId !== null;
    },
    showTestBtn() {
      return auth.userInfo().roles.indexOf("HCI_ADMIN") >= 0
    }
  },
  methods: {
    prettifyFrequency(frequency) {
      if (!frequency) return "";
      return frequency.charAt(0) + frequency.slice(1).toLowerCase();
    },
    userLabel(user) {
      // `fullName` is only present on entries seeded from a group's saved
      // recipients; users fetched from UAM carry firstName/lastName instead.
      const fullName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.fullName || user.username;
      // return `${fullName} (${user.email})`;
      // Spell out why the row is disabled instead of dropping it from the list.
      if (!user.email) return `${fullName} — no email on file`;
      return `${fullName}`;
    },
    userMatchesQuery(user, query) {
      return [
        [user.firstName, user.lastName].filter(Boolean).join(" "),
        user.fullName,
        user.username,
        user.email
      ].some(field => field && field.toLowerCase().indexOf(query) >= 0);
    },
    // Merge by id so a search result never evicts a preloaded or seeded user,
    // and an already-selected recipient never drops out of the option list.
    mergeUsers(fetched) {
      const byId = new Map(this.users.map(user => [user.id, user]));
      (fetched || []).forEach(user => {
        byId.set(user.id, Object.assign({}, byId.get(user.id), user));
      });
      this.users = Array.from(byId.values());
    },
    recipientSummary(group) {
      const names = group.recipients.map(recipient => recipient.fullName);
      if (names.length <= 2) return names.join(", ");
      return `${names.slice(0, 2).join(", ")} +${names.length - 2} more`;
    },
    recipientEmails(group) {
      return group.recipients.map(recipient => recipient.email).join(", ");
    },
    formatDate(isoDate) {
      if (!isoDate) return "";
      return new Date(isoDate).toLocaleDateString();
    },
    // GET /subscription-groups — active groups only, ordered by created ascending.
    async fetchGroups() {
      this.loadingGroups = true;
      this.groups = [];

      try {
        const response = await this.$http.get(this.groupsUrl);
        this.groups = response.body;
      } catch (err) {
        this.errorMessage = this.mapError(err, "Failed to load subscription groups");
      } finally {
        this.loadingGroups = false;
      }
    },
    // GET /report-types — the tenant's available report types, ordered by
    // label. Ids are environment-assigned serials (code is the stable
    // identifier), so they must be resolved here at runtime — never hardcoded.
    // POST/PUT require one of these ids as reportTypeId, so saving is blocked
    // until this fetch succeeds.
    async fetchReportTypes() {
      this.loadingReportTypes = true;
      try {
        const response = await this.$http.get(this.reportTypesUrl);
        this.reportTypes = response.body;
      } catch (err) {
        this.errorMessage = this.mapError(err, "Failed to load report types");
      } finally {
        this.loadingReportTypes = false;
      }
    },
    // Preload one page of active users as the picker's starting options.
    // Pages beyond this are reached by typing (see searchUsers) rather than
    // by paging the whole directory into the browser up front.
    async fetchUsers() {
      this.loadingUsers = true;
      try {
        const response = await this.$http.get(this.usersUrl, {
          params: { status: true, size: USER_PRELOAD_SIZE, page: 1 }
        });
        this.mergeUsers(response.body.content);
      } catch (err) {
        this.errorMessage = this.mapError(err, "Failed to load users for the recipient picker");
      } finally {
        this.loadingUsers = false;
      }
    },
    // el-select's remote-method is debounced 300ms internally, so this fires
    // when typing pauses, not per keystroke. GET /uam/users only supports
    // `username` as a text filter (case-insensitive substring) — there is no
    // server-side name search — so results are merged into the pool and
    // recipientOptions does the name/email matching locally.
    async searchUsers(query) {
      this.userQuery = query || "";
      const trimmed = this.userQuery.trim();
      if (!trimmed) {
        this.searchResultIds = [];
        this.searchingUsers = false;
        return;
      }
      const token = ++this.userSearchToken;
      this.searchingUsers = true;
      try {
        const response = await this.$http.get(this.usersUrl, {
          params: { status: true, size: USER_SEARCH_SIZE, page: 1, username: trimmed }
        });
        if (token !== this.userSearchToken) return;
        const matches = (response.body && response.body.content) || [];
        this.mergeUsers(matches);
        // Record the hits so recipientOptions lists them verbatim — the server
        // matched on `username`, which the displayed name may not contain.
        this.searchResultIds = matches.map(user => user.id);
      } catch (err) {
        if (token === this.userSearchToken) {
          this.errorMessage = this.mapError(err, "Failed to search users");
        }
      } finally {
        if (token === this.userSearchToken) this.searchingUsers = false;
      }
    },
    // el-select clears its own query when the dropdown closes but doesn't
    // re-run remote-method, so clear ours too or the list stays filtered.
    onRecipientDropdownToggle(visible) {
      if (!visible) this.resetUserSearch();
    },
    resetUserSearch() {
      this.userQuery = "";
      this.searchResultIds = [];
    },
    // "All" means every user currently in the option pool — the preloaded page
    // plus anything a search has pulled in — not every user in the directory.
    selectAllRecipients() {
      this.form.recipientUserIds = this.recipientPool.map(user => user.id);
    },
    resetRecipientSelection() {
      this.form.recipientUserIds = [];
    },
    openCreate() {
      this.editingGroupId = null;
      this.form = {
        name: "",
        frequency: "WEEKLY",
        recipientUserIds: [],
        // Preselect when the tenant only has one report type.
        reportTypeId: this.reportTypes.length === 1 ? this.reportTypes[0].id : null
      };
      this.errorMessage = null;
      this.runSummary = null;
      this.resetUserSearch();
      this.view = "form";
    },
    openEdit(group) {
      this.editingGroupId = group.id;
      this.form = {
        name: group.name,
        frequency: group.frequency,
        recipientUserIds: group.recipients.map(recipient => recipient.userId),
        reportTypeId: group.reportType ? group.reportType.id : null
      };
      // Seed the pool from the group's snapshotted recipients so saved
      // recipients outside the preloaded page still render as names rather
      // than raw user ids.
      this.mergeUsers(group.recipients.map(recipient => ({
        id: recipient.userId,
        fullName: recipient.fullName,
        email: recipient.email
      })));
      this.errorMessage = null;
      this.runSummary = null;
      this.resetUserSearch();
      this.view = "form";
    },
    backToList() {
      this.view = "list";
      this.editingGroupId = null;
      this.errorMessage = null;
      this.resetUserSearch();
    },
    save() {
      this.$refs.subscriptionForm.validate(async valid => {
        if (!valid) return;
        this.saving = true;
        this.errorMessage = null;
        // POST creates; PUT is a full replace of name/frequency/recipients/
        // report type. Recipients are resolved server-side against account-api
        // from recipientUserIds (400 INVALID_RECIPIENT for inactive/email-less
        // users, 409 GROUP_LIMIT_EXCEEDED when the quota is full); the report
        // type is validated against the tenant report_type table (400
        // INVALID_REPORT_TYPE for unknown ids).
        const payload = {
          name: this.form.name.trim(),
          frequency: this.form.frequency,
          recipientUserIds: this.form.recipientUserIds,
          reportTypeId: this.form.reportTypeId
        };
        try {
          if (this.editingGroupId) {
            await this.$http.put(`${this.groupsUrl}/${this.editingGroupId}`, payload);
          } else {
            await this.$http.post(this.groupsUrl, payload);
          }
          this.view = "list";
          this.editingGroupId = null;
          await this.fetchGroups();
        } catch (err) {
          this.errorMessage = this.mapError(err, "Failed to save the subscription group");
        } finally {
          this.saving = false;
        }
      });
    },
    // Save, then fire the manual send trigger for the saved group:
    // POST /subscription-groups/{id}/send. The trigger runs the real send
    // pipeline against the persisted group (not the form), ignoring the
    // schedule, and delivers REAL emails to the recipients — so confirm
    // first. The call blocks for the whole run (server hard cap 29s), hence
    // the button spinner. Requires HCI_ADMIN on top of ROLE_HH_ADMIN; the
    // button is gated on that role (see showTestBtn).
    test() {
      this.$refs.subscriptionForm.validate(async valid => {
        if (!valid) return;
        const recipientCount = this.form.recipientUserIds.length;
        try {
          await this.$confirm(
            `This saves the group and immediately sends a real report email to ${recipientCount} recipient${recipientCount === 1 ? "" : "s"}. Continue?`,
            "Send test report now?",
            { confirmButtonText: "Save & Send", cancelButtonText: "Cancel", type: "warning" }
          );
        } catch (canceled) {
          this.$message({ type: "info", message: "Test send canceled" });
          return;
        }

        this.testing = true;
        this.errorMessage = null;
        this.runSummary = null;
        // POST creates; PUT is a full replace of name/frequency/recipients/
        // report type. Recipients are resolved server-side against account-api
        // from recipientUserIds (400 INVALID_RECIPIENT for inactive/email-less
        // users, 409 GROUP_LIMIT_EXCEEDED when the quota is full); the report
        // type is validated against the tenant report_type table (400
        // INVALID_REPORT_TYPE for unknown ids).
        const payload = {
          name: this.form.name.trim(),
          frequency: this.form.frequency,
          recipientUserIds: this.form.recipientUserIds,
          reportTypeId: this.form.reportTypeId
        };
        let stage = "save";
        try {
          let groupId = this.editingGroupId;
          if (groupId) {
            await this.$http.put(`${this.groupsUrl}/${groupId}`, payload);
          } else {
            const created = await this.$http.post(this.groupsUrl, payload);
            groupId = created.body.id;
          }
          stage = "send";
          // Empty body = "as if the schedule fired right now" — the API
          // defaults referenceDate to now and reports on the prior complete
          // period for the group's frequency.
          const run = await this.$http.post(`${this.groupsUrl}/${groupId}/send`, {});
          this.view = "list";
          this.editingGroupId = null;
          await this.fetchGroups();
          this.showRunSummary(run.body);
        } catch (err) {
          if (stage === "send") {
            // The save landed, so return to the list either way and surface
            // the send failure there.
            this.errorMessage = this.mapError(err, "The group was saved, but the test send failed");
            this.view = "list";
            this.editingGroupId = null;
            await this.fetchGroups();
          } else {
            this.errorMessage = this.mapError(err, "Failed to save the subscription group");
          }
        } finally {
          this.testing = false;
        }
      });
    },
    // The trigger returns 200 even when recipients were skipped or the send
    // failed — the outcomes are in-band under status/counts/groups[0]. Rendered
    // as an el-alert in the dialog rather than a MessageBox: element-ui 1.4
    // interpolates MessageBox messages as text (no dangerouslyUseHTMLString),
    // so markup can't be used to lay out the per-recipient lines.
    showRunSummary(summary) {
      const counts = (summary && summary.counts) || {};
      const group = summary && summary.groups && summary.groups[0];
      const sent = counts.recipientsSent || 0;
      const skipped = counts.recipientsSkipped || 0;
      const failed = counts.recipientsFailed || 0;
      const lines = ((group && group.recipients) || []).map(recipient => {
        const detail = recipient.skipReason || recipient.errorMessage;
        return `${recipient.email} — ${recipient.status}${detail ? ` (${detail})` : ""}`;
      });
      // A group-level failure (unknown report type, template not provisioned,
      // metrics error) comes back with no recipient outcomes at all.
      if (group && group.status === "FAILED") {
        lines.push(`Group failed: ${group.errorMessage || "unknown error"}`);
      }
      this.runSummary = {
        clean: summary && summary.status === "COMPLETED" && failed === 0 && !counts.groupsFailed,
        headline: `Test send: ${sent} sent · ${skipped} skipped · ${failed} failed`,
        lines
      };
    },
    confirmDelete(group) {
      this.$confirm(`Delete "${group.name}"? Its recipients will stop receiving scheduled reports.`, "Confirm", {
        confirmButtonText: "Delete",
        cancelButtonText: "Cancel",
        type: "warning"
      })
        .then(async () => {
          this.errorMessage = null;
          // DELETE /subscription-groups/{id} — soft delete, 204.
          try {
            await this.$http.delete(`${this.groupsUrl}/${group.id}`);
            await this.fetchGroups();
          } catch (err) {
            this.errorMessage = this.mapError(err, "Failed to delete the subscription group");
          }
        })
        .catch(() => {
          this.$message({
            type: "info",
            message: "Delete canceled"
          });
        });
    },
    mapError(err, fallback) {
      const body = err && err.body;
      const messagesByCode = {
        GROUP_LIMIT_EXCEEDED: "Subscription group limit reached for this organization's tier.",
        FEATURE_DISABLED: "Hand Hygiene email reports are not enabled for this organization.",
        MISSING_UAM_READ: "Your account is missing the permission needed to look up recipients.",
        UPSTREAM_UNAVAILABLE: "The user lookup service is temporarily unavailable. Please try again."
      };
      if (body && body.code && messagesByCode[body.code]) {
        return messagesByCode[body.code];
      }
      if (body && (body.code === "INVALID_RECIPIENT" || body.code === "INVALID_REPORT_TYPE")) {
        return body.error;
      }
      if (body && body.error) {
        return `${fallback}: ${body.error}`;
      }
      return fallback;
    },
    close() {
      this.$emit("dialog-close");
    }
  },
  async created() {
    this.groupLimit = this.$configStore.maxEmailReports()
  },
  mounted() {
    this.fetchGroups();
    this.fetchUsers();
    this.fetchReportTypes();
  }
};
</script>

<style scoped>
.subscriptions-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 15px;
}

.subscriptions-quota {
  color: #999;
  font-size: 0.85rem;
}

.subscriptions-quota--full {
  color: #ff4949;
  font-weight: bold;
}

.recipients-actions {
  margin-top: 8px;
}

.run-summary-line {
  font-size: 0.85rem;
  line-height: 1.6;
}

.subscriptions-empty {
  margin-top: 15px;
  color: #999;
  text-align: center;
}
</style>
